import React, { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot, query, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase"; // Verifique o caminho para sua config do Firebase
import { useAuth, usePermissions } from "./AuthContext";
import { User, Crown, Eye, Send, X, Mic, MicOff, Video, VideoOff, Shield, ShieldOff, Search } from "lucide-react";
import type RealtimeKitClient from "@cloudflare/realtimekit";

// Tipagem para os dados de um participante
interface Participant {
  id: string; // O UID do usuário
  name: string;
  roleInSession: "representative" | "observer";
  avatarId: string | null;
  role: string; // Role original do usuário (paciente, terapeuta, etc.)
  sessionIdentityKey?: string | null;
  promotionStatus?: "invited" | "accepted" | "declined"; // Estado do convite
}

interface ParticipantsPanelProps {
  roomId: string;
  /** Quem está vendo é moderador (dono ou co-admin)? Libera todas as ações. */
  isModerator?: boolean;
  /** Quem está vendo é o dono da sala? (não pode rebaixar o próprio dono). */
  isOwner?: boolean;
  /** uids dos co-admins promovidos (badge + estado do botão). */
  moderators?: string[];
  currentUserId?: string | null;
  /** Promove/remove co-admin. */
  onSetModerator?: (targetUserId: string, makeModerator: boolean) => Promise<void> | void;
  onKickUser?: (userId: string) => Promise<void>;
  onDisableAudio?: (userId: string) => Promise<void>;
  onDisableVideo?: (userId: string) => Promise<void>;
  meetingClient?: RealtimeKitClient | null;
}

const ParticipantsPanel: React.FC<ParticipantsPanelProps> = ({ roomId, isModerator: isModeratorProp, moderators, currentUserId, onSetModerator, onKickUser, onDisableAudio, onDisableVideo, meetingClient }) => {
  const { user } = useAuth();
  const { userRole } = usePermissions();
  const [allParticipants, setAllParticipants] = useState<Participant[]>([]);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [settingAdminId, setSettingAdminId] = useState<string | null>(null);
  const [roomCreatedBy, setRoomCreatedBy] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [participantStates, setParticipantStates] = useState<Record<string, { audioEnabled: boolean; videoEnabled: boolean }>>({});

  const moderatorSet = useMemo(() => new Set(moderators ?? []), [moderators]);
  const myUid = currentUserId ?? user?.uid ?? null;

  const isParticipantAdmin = (participantId: string) =>
    participantId === roomCreatedBy || moderatorSet.has(participantId);

  // Fallback (compatibilidade): se o pai não passar isModerator, usa a regra antiga
  // do dono terapeuta. Quando o Room passa isModerator, co-admins também moderam.
  const isOwnerSelf = userRole === "terapeuta" && roomCreatedBy === user?.uid;
  const canModerate = isModeratorProp ?? isOwnerSelf;

  const buildParticipantDedupKey = (participant: Participant) => {
    // Prioriza a chave estável da sessão para reconexões com novo uid.
    // Fallback: nome normalizado + papel global (que não muda com promoção).
    if (participant.sessionIdentityKey) {
      return `session:${participant.sessionIdentityKey}`;
    }

    return `${participant.name.trim().toLowerCase()}::${participant.role.trim().toLowerCase()}`;
  };

  const representatives = useMemo(() => {
    return allParticipants
      .filter((p) => p.roleInSession === "representative")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allParticipants]);

  const observers = useMemo(() => {
    return allParticipants
      .filter((p) => p.roleInSession === "observer")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allParticipants]);

  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (p: Participant) =>
    normalizedSearch === "" || p.name.toLowerCase().includes(normalizedSearch);

  const filteredRepresentatives = useMemo(
    () => representatives.filter(matchesSearch),
    [representatives, normalizedSearch],
  );
  const filteredObservers = useMemo(
    () => observers.filter(matchesSearch),
    [observers, normalizedSearch],
  );

  // Efeito para obter o criador da sala
  useEffect(() => {
    if (!roomId) return;

    const roomRef = doc(db, "rooms", roomId);
    const unsubscribe = onSnapshot(roomRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setRoomCreatedBy(data.createdBy || null);
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  // Efeito para ouvir as mudanças na subcoleção 'participants' em tempo real
  useEffect(() => {
    if (!roomId) return;

    const participantsColRef = collection(db, "rooms", roomId, "participants");
    const q = query(participantsColRef);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const participantsByKey = new Map<string, Participant>();

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.name) {
            const participant = {
              id: doc.id,
              name: data.name || "Usuário sem nome",
              roleInSession: data.roleInSession || "observer",
              avatarId: data.avatarId || null,
              role: data.role || "participant",
              sessionIdentityKey: data.sessionIdentityKey || null,
              promotionStatus: data.promotionStatus, // Lê o novo campo
            } as Participant;

            const dedupKey = buildParticipantDedupKey(participant);
            const existingParticipant = participantsByKey.get(dedupKey);

            if (!existingParticipant) {
              participantsByKey.set(dedupKey, participant);
              return;
            }

            // Quando há dois docs com a mesma identidade (reconexão com uid diferente),
            // mantém o registro de maior privilégio: representative > observer.
            // Em empate de roleInSession, prefere o que tem promotionStatus=invited ou avatar.
            const currentIsRepresentative = participant.roleInSession === "representative";
            const existingIsRepresentative = existingParticipant.roleInSession === "representative";
            const shouldReplaceExisting =
              (currentIsRepresentative && !existingIsRepresentative) ||
              (!existingIsRepresentative && !currentIsRepresentative && (
                participant.promotionStatus === "invited" ||
                (!!participant.avatarId && !existingParticipant.avatarId)
              ));

            if (shouldReplaceExisting) {
              participantsByKey.set(dedupKey, participant);
            }
          }
        });

        setAllParticipants(Array.from(participantsByKey.values()));
      },
      (err) => {
        console.error("Erro ao buscar participantes:", err);
        setError("Não foi possível carregar a lista de participantes.");
      }
    );

    return () => unsubscribe(); // Limpa o listener ao desmontar
  }, [roomId]);

  useEffect(() => {
    if (!meetingClient) return;

    const updateParticipantStates = () => {
      const participants = meetingClient.participants.joined.toArray();
      const states: Record<string, { audioEnabled: boolean; videoEnabled: boolean }> = {};

      participants.forEach((peer) => {
        const userId = peer.customParticipantId;
        if (userId) {
          states[userId] = {
            audioEnabled: peer.audioEnabled ?? false,
            videoEnabled: peer.videoEnabled ?? false,
          };
        }
      });

      setParticipantStates(states);
    };

    updateParticipantStates();

    // Single handler for all media updates
    const handleMediaUpdate = () => updateParticipantStates();

    // Listen at the participants collection level, not individual peers
    const participants = meetingClient.participants.joined;

    participants.on("participantJoined", updateParticipantStates);
    participants.on("participantLeft", updateParticipantStates);
    participants.on("audioUpdate", handleMediaUpdate);
    participants.on("videoUpdate", handleMediaUpdate);

    return () => {
      participants.off("participantJoined", updateParticipantStates);
      participants.off("participantLeft", updateParticipantStates);
      participants.off("audioUpdate", handleMediaUpdate);
      participants.off("videoUpdate", handleMediaUpdate);
    };
  }, [meetingClient]);

  // Função para enviar o convite de promoção
  const handlePromote = async (userIdToPromote: string) => {
    if (promotingUserId) return;

    setPromotingUserId(userIdToPromote);
    setError("");

    try {
      console.log(`✉️ Enviando convite para ${userIdToPromote} na sala ${roomId}`);
      const participantRef = doc(db, "rooms", roomId, "participants", userIdToPromote);

      // Define o 'status de promoção' como 'invited'. O Room.tsx do participante vai reagir a isto.
      await updateDoc(participantRef, {
        promotionStatus: "invited",
      });
      // A UI será atualizada automaticamente pelo listener onSnapshot,
      // então não precisamos redefinir o promotingUserId aqui.
    } catch (err: any) {
      console.error("Erro ao enviar convite:", err);
      setError("Ocorreu um erro ao enviar o convite.");
    }

    setPromotingUserId(null); // Reseta em caso de erro
  };

  // Promove/remove co-admin (administrador da sala).
  const handleSetAdmin = async (targetUserId: string, makeAdmin: boolean) => {
    if (!onSetModerator || !canModerate || settingAdminId) return;
    // Nunca mexer no dono nem em si mesmo via este botão.
    if (targetUserId === roomCreatedBy || targetUserId === myUid) return;

    setSettingAdminId(targetUserId);
    setError("");
    try {
      await onSetModerator(targetUserId, makeAdmin);
    } catch (err) {
      console.error("Erro ao atualizar administrador:", err);
      setError("Não foi possível atualizar o administrador.");
    } finally {
      setSettingAdminId(null);
    }
  };

  // Função para despromover (voltar de representative para observer)
  const handleDemote = async (userIdToDemote: string) => {
    if (!user || !canModerate || userIdToDemote === user.uid) {
      return;
    }

    setError("");

    try {
      console.log(`👁️ Despromovendo ${userIdToDemote} de volta para observador na sala ${roomId}`);
      const participantRef = doc(db, "rooms", roomId, "participants", userIdToDemote);

      // Atualiza para observer e remove o avatar
      await updateDoc(participantRef, {
        roleInSession: "observer",
        avatarId: null,
        promotionStatus: null,
      });

      console.log(`✅ Usuário ${userIdToDemote} despromovido para observador`);
    } catch (err: any) {
      console.error("Erro ao despromover usuário:", err);
      setError("Ocorreu um erro ao despromover o usuário.");
    }
  };

  const handleKick = async (userIdToKick: string) => {
    if (!onKickUser || !user || !canModerate || userIdToKick === user.uid) {
      return;
    }

    try {
      await onKickUser(userIdToKick);
    } catch (error) {
      setError("Erro ao remover usuário da sala.");
    }
  };

  const handleDisableAudio = async (userId: string) => {
    if (!onDisableAudio || !user || !canModerate || userId === user.uid) {
      return;
    }

    try {
      await onDisableAudio(userId);
      // Atualiza o estado imediatamente após desativar
      if (meetingClient) {
        const participants = meetingClient.participants.joined.toArray();
        const peer = participants.find((p: any) => p.customParticipantId === userId);
        if (peer) {
          setParticipantStates((prev) => ({
            ...prev,
            [userId]: {
              audioEnabled: false,
              videoEnabled: prev[userId]?.videoEnabled ?? peer.videoEnabled ?? false,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Erro ao desativar áudio:", error);
      setError("Erro ao desativar áudio do usuário.");
    }
  };

  const handleDisableVideo = async (userId: string) => {
    if (!onDisableVideo || !user || !canModerate || userId === user.uid) {
      return;
    }

    try {
      await onDisableVideo(userId);
      // Atualiza o estado imediatamente após desativar
      if (meetingClient) {
        const participants = meetingClient.participants.joined.toArray();
        const peer = participants.find((p: any) => p.customParticipantId === userId);
        if (peer) {
          setParticipantStates((prev) => ({
            ...prev,
            [userId]: {
              audioEnabled: prev[userId]?.audioEnabled ?? peer.audioEnabled ?? false,
              videoEnabled: false,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Erro ao desativar vídeo:", error);
      setError("Erro ao desativar vídeo do usuário.");
    }
  };

  const getParticipantAudioState = (userId: string): boolean | null => {
    if (!meetingClient) return null;
    const state = participantStates[userId];
    if (state !== undefined) {
      return state.audioEnabled;
    }
    // Fallback para o método antigo se o estado não estiver disponível
    const participants = meetingClient.participants.joined.toArray();
    const peer = participants.find((p: any) => p.customParticipantId === userId);
    return peer?.audioEnabled ?? null;
  };

  const getParticipantVideoState = (userId: string): boolean | null => {
    if (!meetingClient) return null;
    const state = participantStates[userId];
    if (state !== undefined) {
      return state.videoEnabled;
    }
    // Fallback para o método antigo se o estado não estiver disponível
    const participants = meetingClient.participants.joined.toArray();
    const peer = participants.find((p: any) => p.customParticipantId === userId);
    return peer?.videoEnabled ?? null;
  };

  const canKick = canModerate && !!onKickUser;
  const canDisableAudio = canModerate && !!onDisableAudio && !!meetingClient;
  const canDisableVideo = canModerate && !!onDisableVideo && !!meetingClient;
  const canDemote = canModerate; // Pode despromover se for moderador (dono ou co-admin)
  const canManageAdmins = canModerate && !!onSetModerator;

  // Botão de admin: visível para moderadores, em participantes que não são o dono
  // nem o próprio usuário.
  const renderAdminButton = (participantId: string) => {
    if (!canManageAdmins) return null;
    if (participantId === roomCreatedBy || participantId === myUid) return null;

    const alreadyAdmin = moderatorSet.has(participantId);
    const isBusy = settingAdminId === participantId;

    return (
      <button
        onClick={() => handleSetAdmin(participantId, !alreadyAdmin)}
        disabled={isBusy}
        className={`text-white text-xs font-bold px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 shrink-0 ${
          isBusy
            ? "bg-gray-400 cursor-not-allowed"
            : alreadyAdmin
              ? "bg-gray-600 hover:bg-gray-700"
              : "bg-indigo-600 hover:bg-indigo-700"
        }`}
        title={alreadyAdmin ? "Remover administrador" : "Tornar administrador"}>
        {isBusy ? (
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
        ) : alreadyAdmin ? (
          <ShieldOff className="h-3 w-3" />
        ) : (
          <Shield className="h-3 w-3" />
        )}
        {isBusy ? "..." : alreadyAdmin ? "Remover admin" : "Tornar admin"}
      </button>
    );
  };

  const renderAdminBadge = (participantId: string) => {
    if (!isParticipantAdmin(participantId)) return null;
    const isOwnerBadge = participantId === roomCreatedBy;
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
          isOwnerBadge ? "bg-yellow-100 text-yellow-700" : "bg-indigo-100 text-indigo-700"
        }`}
        title={isOwnerBadge ? "Dono da sala" : "Administrador"}>
        <Shield className="h-2.5 w-2.5" />
        {isOwnerBadge ? "Dono" : "Admin"}
      </span>
    );
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col">
      <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Participantes</h3>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 p-2 rounded mb-4 text-sm">
          {error}
          <button onClick={() => setError("")} className="ml-2 text-red-600 hover:text-red-800 font-bold">
            ×
          </button>
        </div>
      )}

      {/* Busca por nome — útil em salas com muitas pessoas. */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar participante por nome..."
          className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Limpar busca">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Seção de Representantes */}
      <div>
        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Crown className="h-5 w-5 text-yellow-500" />
          Representantes ({filteredRepresentatives.length}{normalizedSearch ? ` de ${representatives.length}` : ""})
        </h4>
        <ul className="space-y-2">
          {filteredRepresentatives.length === 0 ? (
            <li className="text-sm text-gray-500 italic">{normalizedSearch ? "Nenhum representante encontrado" : "Nenhum representante ativo"}</li>
          ) : (
            filteredRepresentatives.map((p) => {
              const audioEnabled = getParticipantAudioState(p.id);
              const videoEnabled = getParticipantVideoState(p.id);
              return (
                <li key={p.id} className="bg-gray-50 p-2 rounded-md space-y-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <User className="h-5 w-5 text-gray-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-gray-800 font-medium truncate">{p.name}</span>
                        {renderAdminBadge(p.id)}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.role}</div>
                    </div>
                  </div>
                  {p.id !== myUid && (
                    <div className="flex items-center gap-1.5 flex-wrap pl-8">
                      {canDisableAudio && (
                        <button
                          onClick={() => handleDisableAudio(p.id)}
                          className={`p-1 rounded transition-colors ${
                            audioEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                          }`}
                          title={audioEnabled ? "Desativar microfone" : "Microfone desativado"}
                          disabled={!audioEnabled}>
                          {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                        </button>
                      )}
                      {canDisableVideo && (
                        <button
                          onClick={() => handleDisableVideo(p.id)}
                          className={`p-1 rounded transition-colors ${
                            videoEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                          }`}
                          title={videoEnabled ? "Desativar câmera" : "Câmera desativada"}
                          disabled={!videoEnabled}>
                          {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                        </button>
                      )}
                      {renderAdminButton(p.id)}
                      {canDemote && (
                        <button
                          onClick={() => handleDemote(p.id)}
                          className="text-white text-xs font-bold px-3 py-1 rounded-full bg-orange-500 hover:bg-orange-600 transition-colors shrink-0"
                          title="Voltar para observador">
                          Rebaixar
                        </button>
                      )}
                      {canKick && (
                        <button
                          onClick={() => handleKick(p.id)}
                          className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                          title="Remover da sala">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>

      {/* Seção de Observadores */}
      <div className="mt-6 pt-4 border-t flex-1 overflow-y-auto">
        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-500" />
          Observadores ({filteredObservers.length}{normalizedSearch ? ` de ${observers.length}` : ""})
        </h4>
        <ul className="space-y-2">
          {filteredObservers.length === 0 ? (
            <li className="text-sm text-gray-500 italic">{normalizedSearch ? "Nenhum observador encontrado" : "Nenhum observador"}</li>
          ) : (
            filteredObservers.map((p) => {
              const isPromotingThisUser = promotingUserId === p.id;
              const wasInvited = p.promotionStatus === "invited";
              const audioEnabled = getParticipantAudioState(p.id);
              const videoEnabled = getParticipantVideoState(p.id);

              return (
                <li key={p.id} className="bg-gray-50 p-2 rounded-md space-y-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <User className="h-5 w-5 text-gray-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-gray-800 font-medium truncate">{p.name}</span>
                        {renderAdminBadge(p.id)}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.role}</div>
                    </div>
                  </div>
                  {(canModerate || p.id !== myUid) && (
                    <div className="flex items-center gap-1.5 flex-wrap pl-8">
                      {canModerate && (
                        <button
                          onClick={() => handlePromote(p.id)}
                          disabled={isPromotingThisUser || wasInvited}
                          className={`text-white text-xs font-bold px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 shrink-0 ${
                            isPromotingThisUser || wasInvited ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
                          }`}
                          title={wasInvited ? "Convite já enviado" : "Promover para representante"}>
                          {isPromotingThisUser ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : wasInvited ? (
                            <Send className="h-3 w-3" />
                          ) : null}
                          {isPromotingThisUser ? "Promovendo..." : wasInvited ? "Aguardando" : "Promover"}
                        </button>
                      )}
                      {p.id !== myUid && (
                        <>
                          {renderAdminButton(p.id)}
                          {canDisableAudio && (
                            <button
                              onClick={() => handleDisableAudio(p.id)}
                              className={`p-1 rounded transition-colors ${
                                audioEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                              }`}
                              title={audioEnabled ? "Desativar microfone" : "Microfone desativado"}
                              disabled={!audioEnabled}>
                              {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                            </button>
                          )}
                          {canDisableVideo && (
                            <button
                              onClick={() => handleDisableVideo(p.id)}
                              className={`p-1 rounded transition-colors ${
                                videoEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                              }`}
                              title={videoEnabled ? "Desativar câmera" : "Câmera desativada"}
                              disabled={!videoEnabled}>
                              {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                            </button>
                          )}
                          {canKick && (
                            <button
                              onClick={() => handleKick(p.id)}
                              className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                              title="Remover da sala">
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default ParticipantsPanel;
